import React from 'react';
import { Controller, FieldError, FieldErrorsImpl, Merge, useFormContext } from 'react-hook-form';
import SelectPill from '../../../../../../components/SelectPill';
import arrowRightIcon from '../../../../../../assets/icons/icon_arrow-right-gray.svg';
import { LOCAL_TYPE_LIST } from '../../constants';
import styles from './LocalSelect.module.scss';
import { useOmnichannelFilter } from '../../hooks/useOmnichannelFilter';
import { useOmnichannelStore } from '../../store/ModuloOmnichannel.store';
import { LocaleType, LocalType, SelectList } from '../../types';

type LocalTypeSelectComponentProps = {
  locale: LocaleType;
  data: SelectList[];
  value: string[];
  placeholder: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  filterIsEmpty: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  name: string;
  renderMenu: (menu: React.ReactNode) => React.ReactNode;
  onOpen: () => void;
  onSearch: (value: string) => void;
  onSelect: (value: string[]) => void;
  onClean: () => void;
};

type LocalSelectProps = {
  locale: LocaleType;
  renderMenu: (menu: React.ReactNode) => React.ReactNode;
  placeholder: (name: string, list: string[]) => string;
};

type LocalFilterField = 'cidade' | 'uf';

const LocalTypeSelectComponent = ({
  locale,
  filterIsEmpty,
  data,
  value,
  placeholder,
  name,
  onClean,
  renderMenu,
  onSearch,
  onOpen,
  onSelect,
}: LocalTypeSelectComponentProps) => (
  <SelectPill
    type="check"
    id={filterIsEmpty ? 'select-pill-is-empty' : 'check-picker-pill'}
    name={name}
    placeholder={placeholder}
    value={value}
    data={data}
    onSearch={onSearch}
    onSelect={onSelect}
    onOpen={onOpen}
    onClean={onClean}
    locale={locale}
    renderMenu={renderMenu}
  />
);

export const LocalSelect = ({ locale, renderMenu, placeholder }: LocalSelectProps) => {
  const localFilterType = useOmnichannelStore(state => state.localFilterType);
  const setOmnichannelState = useOmnichannelStore(state => state.setOmnichannelState);
  const setOmnichannelMutateRequestBody = useOmnichannelStore(state => state.setOmnichannelMutateRequestBody);

  const { cidade, uf, filterHandlers } = useOmnichannelFilter();

  const LOCAL_FILTER_BY_FIELD: Record<LocalFilterField, { tipo: 'CIDADE' | 'UF'; filter: typeof cidade }> = {
    cidade: { tipo: 'CIDADE', filter: cidade },
    uf: { tipo: 'UF', filter: uf },
  };

  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();
  const tipoLocalizacao = watch('tipoLocalizacao');

  const handleLocationTypeOnSelect = (value: LocalType) => {
    setOmnichannelState('localFilterType', value);

    const allTerritory = value === 'TODO_TERRITORIO';
    const nextUfs = allTerritory ? (['TODO_TERRITORIO'] as const) : [];

    setOmnichannelMutateRequestBody('ufs', [...nextUfs]);
    setOmnichannelMutateRequestBody('cidades', []);
    setValue('uf', [...nextUfs]);
    setValue('cidade', []);
  };

  const renderLocalFilterSelect = (fieldName: LocalFilterField) => {
    const { tipo, filter } = LOCAL_FILTER_BY_FIELD[fieldName];
    const fieldError = errors?.[fieldName];

    return (
      <Controller
        key={fieldName}
        name={fieldName}
        control={control}
        render={({ field }) => (
          <LocalTypeSelectComponent
            filterIsEmpty={fieldError}
            placeholder={fieldError?.message || placeholder(fieldName, filter?.value)}
            name={fieldName}
            data={filter?.list ?? []}
            value={field.value}
            onSearch={search => filterHandlers.onSearchFilter(fieldName, search)}
            onSelect={value => {
              filterHandlers.handleSelectFilter(fieldName, value);
              if (tipoLocalizacao === tipo) setValue(fieldName, value, { shouldValidate: true });
            }}
            onOpen={() => filterHandlers.onSearchFilter(fieldName, '')}
            onClean={() => {
              filterHandlers.handleClearFilters(fieldName);
              if (tipoLocalizacao === tipo) setValue(fieldName, [], { shouldValidate: true });
            }}
            locale={locale}
            renderMenu={renderMenu}
          />
        )}
      />
    );
  };

  return (
    <div className={styles['select-pill-wrapper']}>
      <Controller
        name="tipoLocalizacao"
        control={control}
        render={({ field }) => (
          <SelectPill
            id={errors?.tipoLocalizacao ? 'select-pill-is-empty' : 'select-picker-pill'}
            placeholder="Escolha uma opção"
            name="tipoLocalizacao"
            cleanable={false}
            data={LOCAL_TYPE_LIST}
            value={field.value}
            onSelect={(value: LocalType) => {
              field.onChange(value);
              handleLocationTypeOnSelect(value);
            }}
            locale={locale}
          />
        )}
      />
      {localFilterType !== 'TODO_TERRITORIO' ? (
        <>
          <img src={arrowRightIcon} alt="" className={styles['arrow-right-icon']} />
          {localFilterType === 'CIDADE' ? renderLocalFilterSelect('cidade') : null}
          {localFilterType === 'UF' ? renderLocalFilterSelect('uf') : null}
        </>
      ) : null}
    </div>
  );
};
